import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "../../core/data.service";
import {
  EnvironmentServiceConfiguration,
  ServiceConfigurationKey,
  TenantEnvironmentServiceConfiguration,
} from "../models/configuration-setting";
import { State, toODataString } from "@progress/kendo-data-query";
import { switchMap } from "rxjs/operators";
import { GridDataResult } from "@progress/kendo-angular-grid";
import { ODataResponse } from "../models/odata-response";
import { ODataSingleResponse } from "../models/odata-single-response";
import { Service } from "../models/service";
import { of } from "rxjs";
import { AppConfigService } from 'src/app/core/app-config.service';

@Injectable({
  providedIn: "root",
})
export class ServiceConfigurationKeysDataService extends DataService<
  ServiceConfigurationKey
> {
  constructor(http: HttpClient, acs: AppConfigService) {
    super(http, "ServiceConfigurationKeys", acs);
  }

  getSecret(
    serviceConfigurationKeyId: number
  ) {
    let url = `${this.acs.current.configApiUrl}odata/ServiceConfigurationKeys(${serviceConfigurationKeyId})/Cfg.SecretValue`;
    return this.http.get<ODataSingleResponse<string>>(url);
  }

  keyCount(
    serviceId: number,
    categoryId: number,
    configKeyId: number,
    name: string
  ) {
    let url = `${this.acs.current.configApiUrl}odata/ServiceConfigurationKeys?$filter=Name eq '${name}' and CategoryId eq ${categoryId} and ServiceId eq ${serviceId} and Id ne ${configKeyId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<ServiceConfigurationKey>>(url);
  }

  keyCountByService(
    serviceId: number
  ) {
    let url = `${this.acs.current.configApiUrl}odata/ServiceConfigurationKeys?$filter=ServiceId eq ${serviceId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<ServiceConfigurationKey>>(url);
  }  

  getFlat(
    state: State,
    serviceId: number,
    environmentServiceId: number,
    tenantEnvironmentId: number
  ) {
    // tslint:disable-next-line: max-line-length
    // https://localhost:44327/odata/ServiceConfigurationKeys?$expand=EnvironmentServiceConfiguration($filter=EnvironmentServiceId eq 2)&$filter=ServiceId eq 3
    const odataUrl = toODataString(state);
    let url = `${this.acs.current.configApiUrl}odata/ServiceConfigurationKeys`;
    url += `?$filter=ServiceId eq ${serviceId}`;
    url += `&$expand=Category`;
    if (environmentServiceId != null) {
      url += `,EnvironmentServiceConfiguration($filter=EnvironmentServiceId eq ${environmentServiceId})`;
      if (tenantEnvironmentId != null) {
        url += `,TenantEnvironmentServiceConfiguration($filter=TenantEnvironmentId eq ${tenantEnvironmentId})`;
      }
    }
    url += `&${odataUrl}&$count=true`;

    return this.http.get<ODataResponse<ServiceConfigurationKey>>(url).pipe(
      switchMap((response) => {
        const gridResult: GridDataResult = {
          data: [...response.value],
          total: response["@odata.count"],
        };
        return of(gridResult);
      })
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class EnvironmentServiceConfigurationDataService extends DataService<
  EnvironmentServiceConfiguration
> {
  constructor(http: HttpClient, acs: AppConfigService) {
    super(http, "EnvironmentServiceConfiguration", acs);
  }

  getSecret(
    envServiceConfigId: number
  ) {
    let url = `${this.acs.current.configApiUrl}odata/EnvironmentServiceConfiguration(${envServiceConfigId})/Cfg.SecretValue`;
    return this.http.get<ODataSingleResponse<string>>(url);
  }

  overrideCount(
    configKeyId: number,
  ) {
    let url = `${this.acs.current.configApiUrl}odata/EnvironmentServiceConfiguration?$filter=ServiceConfigurationKeyId eq ${configKeyId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<EnvironmentServiceConfiguration>>(url);
  }

  overrideCountByEnvironmentService(
    environmentServiceId: number,
  ) {
    let url = `${this.acs.current.configApiUrl}odata/EnvironmentServiceConfiguration?$filter=EnvironmentServiceId eq ${environmentServiceId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<EnvironmentServiceConfiguration>>(url);
  }
}

@Injectable({
  providedIn: "root",
})
export class TenantEnvironmentServiceConfigurationDataService extends DataService<
  TenantEnvironmentServiceConfiguration
> {
  constructor(http: HttpClient, acs: AppConfigService) {
    super(http, "TenantEnvironmentServiceConfiguration", acs);
  }

  getSecret(
    tenantEnvServiceConfigId: number
  ) {
    let url = `${this.acs.current.configApiUrl}odata/TenantEnvironmentServiceConfiguration(${tenantEnvServiceConfigId})/Cfg.SecretValue`;
    return this.http.get<ODataSingleResponse<string>>(url);
  }

  overrideCount(
    configKeyId: number,
  ) {
    let url = `${this.acs.current.configApiUrl}odata/TenantEnvironmentServiceConfiguration?$filter=ServiceConfigurationKeyId eq ${configKeyId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<TenantEnvironmentServiceConfiguration>>(url);
  }
  
  overrideCountByTenantEnvironment(
    tenantEnvironmentId: number,
  ) {
    let url = `${this.acs.current.configApiUrl}odata/TenantEnvironmentServiceConfiguration?$filter=TenantEnvironmentId eq ${tenantEnvironmentId}&$count=true&$top=0`;
    return this.http.get<ODataResponse<TenantEnvironmentServiceConfiguration>>(url);
  }   
}
